import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { RootState } from '../store';
import { setItems, addItem, updateItem, removeItem } from '../store/slices/itemsSlice';
import { api, resolveFileUrl } from '../services/api';
import ItemModal from '../components/ItemModal';
import ItemDetailView from '../components/ItemDetailView';
import PasswordLockModal from '../components/PasswordLockModal';
import AdvancedFilters, { FilterState } from '../components/AdvancedFilters';
import useCurrency from '../hooks/useCurrency';
import useI18n from '../hooks/useI18n';
import { compressImages } from '../utils/imageCompression';
import { usePlanLimits } from '../hooks/usePlanLimits';
import LimitReachedModal from '../components/LimitReachedModal';
import { PlatformPlanType } from '../types/plans';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faTrash, 
  faEllipsisH, 
  faArrowRight, 
  faTimes, 
  faBox, 
  faWandMagicSparkles,
  faFilter,
  faGrip,
  faList,
  faMapMarkerAlt,
  faRulerCombined,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import { formatQuantity } from '../config/unitOfMeasure';
import '../styles/Library.css';
import { trackFeature } from '../services/telemetryService';

interface Category {
  id: string;
  name: string;
  color?: string;
  isDefault?: boolean;
}

interface Currency {
  code: string;
  label: string;
  symbol: string;
}

const Library = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items } = useSelector((state: RootState) => state.items);
  const { user } = useSelector((state: RootState) => state.auth);
  
  // Get user's default currency
  const userDefaultCurrency = (user as any)?.defaultCurrency || 'USD';
  const { formatPrice } = useCurrency(userDefaultCurrency);
  const { t } = useI18n();
  const tc = (name: string) => { const key = `categoryName.${name}` as any; const translated = t(key); return translated !== key ? translated : name; };
  const conditionLabel = (c: string) => {
    const map: Record<string, string> = { 'NEW': t('condition.new'), 'LIKE_NEW': t('condition.likeNew'), 'GOOD': t('condition.good'), 'FAIR': t('condition.fair'), 'POOR': t('condition.poor'), 'EXCELLENT': t('condition.excellent') };
    return map[c] || c;
  };
  const { checkItemLimit, showLimitModal, limitModalType, limitCurrent, limitMax, closeLimitModal, currentPlan } = usePlanLimits((user?.planType as PlatformPlanType) || 'FREE');
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [imageErrorMap, setImageErrorMap] = useState<Record<string, boolean>>({});
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockTargetItem, setLockTargetItem] = useState<any | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [itemToMove, setItemToMove] = useState<any | null>(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    condition: [],
    valueRange: [0, 10000],
    purchaseDateFrom: '',
    purchaseDateTo: '',
    aiValueTrend: '',
    locations: [],
    tags: [],
    hasAIValue: false,
    favoritesOnly: false,
    minItems: 1,
    maxItems: 1000,
    sortBy: 'newest',
    unitTypes: []
  });
  const [sortBy, setSortBy] = useState<string>('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [itemsRes, categoriesRes, currenciesRes] = await Promise.all([
        api.get('/items?take=1000'),
        api.get('/categories'),
        api.get('/metadata/currencies'),
      ]);
      // Keep all items including locked ones (they'll show with lock icon)
      const allItems = itemsRes.data.items || [];
      dispatch(setItems({ items: allItems, total: allItems.length }));
      setCategories(categoriesRes.data);
      setCurrencies(currenciesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update selected item when items change (e.g., after favorite toggle)
  useEffect(() => {
    if (selectedItem) {
      const updated = items.find(i => i.id === selectedItem.id);
      if (updated) {
        setSelectedItem(updated);
      }
    }
  }, [items, selectedItem]);

  // Handle edit request from other pages (Favorites, Collections, etc.)
  useEffect(() => {
    const state = location.state as any;
    if (state?.editItem && !loading) {
      handleOpenModal(state.editItem);
      // Clear state so it doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
  }, [location.state, loading]);

  // Handle URL query params from Categories page (?categoryId=X, ?item=X)
  useEffect(() => {
    if (loading || items.length === 0) return;

    const categoryIdParam = searchParams.get('categoryId');
    const itemIdParam = searchParams.get('item');

    if (categoryIdParam) {
      setSelectedCategory(categoryIdParam);
      // Clear the param
      searchParams.delete('categoryId');
      setSearchParams(searchParams, { replace: true });
    }

    if (itemIdParam) {
      const targetItem = items.find(i => i.id === itemIdParam);
      if (targetItem) {
        handleViewItem(targetItem);
      }
      // Clear the param
      searchParams.delete('item');
      setSearchParams(searchParams, { replace: true });
    }
  }, [loading, items, searchParams, setSearchParams]);

  const handleOpenModal = (item: any | null = null) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleSaveItem = async (itemData: any, files: File[] = []) => {
    try {
      // Check item limit when creating (not editing)
      if (!editingItem && !checkItemLimit(items.length)) return;
      const formDataToSend = new FormData();
      formDataToSend.append('name', itemData.name);
      formDataToSend.append('description', itemData.description || '');
      if (itemData.categoryId && itemData.categoryId !== '') {
        formDataToSend.append('categoryId', itemData.categoryId);
      }
      formDataToSend.append('condition', itemData.condition);
      formDataToSend.append('location', itemData.location || '');
      formDataToSend.append('purchasePlace', itemData.purchasePlace || '');
      formDataToSend.append('purchaseCurrency', itemData.purchaseCurrency || 'USD');
      if (itemData.purchasePrice) {
        formDataToSend.append('purchasePrice', itemData.purchasePrice.toString());
      }
      if (itemData.purchaseDate) {
        formDataToSend.append('purchaseDate', itemData.purchaseDate);
      }
      if (itemData.purchaseTime) {
        formDataToSend.append('purchaseTime', itemData.purchaseTime);
      }
      if (itemData.purchaseTimezone) {
        formDataToSend.append('purchaseTimezone', itemData.purchaseTimezone);
      }
      if (itemData.tags && itemData.tags.length > 0) {
        formDataToSend.append('tags', JSON.stringify(itemData.tags));
      }
      if (itemData.unitType) {
        formDataToSend.append('unitType', itemData.unitType);
      }
      if (itemData.unitSubtype) {
        formDataToSend.append('unitSubtype', itemData.unitSubtype);
      }
      if (itemData.unitQuantity !== undefined && itemData.unitQuantity !== '') {
        formDataToSend.append('unitQuantity', itemData.unitQuantity.toString());
      }

      const uploadImages = async (itemId: string, uploadFiles: File[]) => {
        if (!uploadFiles || uploadFiles.length === 0) return;
        // Compress images before uploading — dramatically reduces upload time
        const compressed = await compressImages(uploadFiles);
        const uploadData = new FormData();
        compressed.forEach((uploadFile) => uploadData.append('images', uploadFile));
        await api.post(`/items/${itemId}/images`, uploadData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      };

      if (editingItem) {
        // Update existing item
        const response = await api.put(`/items/${editingItem.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        dispatch(updateItem(response.data));
        trackFeature('item_updated', { itemId: editingItem.id });

        try {
          await uploadImages(editingItem.id, files);
        } catch (imgError) {
          console.error('Image upload failed:', imgError);
        }
        if (files.length > 0) {
          const refreshed = await api.get(`/items/${editingItem.id}`);
          dispatch(updateItem(refreshed.data));
        }
      } else {
        // Create new item
        const response = await api.post('/items', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        dispatch(addItem(response.data));
        trackFeature('item_created', { itemId: response.data.id });

        try {
          await uploadImages(response.data.id, files);
        } catch (imgError) {
          console.error('Image upload failed:', imgError);
        }
        if (files.length > 0) {
          const refreshed = await api.get(`/items/${response.data.id}`);
          dispatch(updateItem(refreshed.data));
        }
      }
    } catch (error) {
      console.error('Failed to save item:', error);
      throw error;
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!window.confirm(t('library.confirmDelete'))) return;
    try {
      await api.delete(`/items/${itemId}`);
      dispatch(removeItem(itemId));
      trackFeature('item_deleted', { itemId });
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
  };

  const toggleFavorite = async (itemId: string) => {
    // Optimistic update for immediate UI feedback
    const existing = items.find(i => i.id === itemId);
    if (existing) {
      dispatch(updateItem({ ...existing, isFavorite: !existing.isFavorite } as any));
    }

    try {
      const response = await api.patch(`/items/${itemId}/favorite`);
      dispatch(updateItem(response.data));
      trackFeature('item_favorite_toggled', { itemId, isFavorite: response.data.isFavorite });
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Roll back if failed
      if (existing) {
        dispatch(updateItem(existing as any));
      }
    }
  };

  const handleViewItem = (item: any) => {
    if (item.isLocked) {
      setLockTargetItem(item);
      setShowLockModal(true);
    } else {
      setSelectedItem(item);
      setShowDetailView(true);
    }
  };

  const handleLockSuccess = () => {
    setShowLockModal(false);
    if (lockTargetItem) {
      setSelectedItem(lockTargetItem);
      setShowDetailView(true);
      setLockTargetItem(null);
    }
  };

  const handleCloseDetailView = () => {
    setShowDetailView(false);
    setSelectedItem(null);
  };

  const handleEditFromDetail = (item: any) => {
    setShowDetailView(false);
    setSelectedItem(null);
    handleOpenModal(item);
  };

  const handleDeleteFromDetail = async (itemId: string) => {
    await handleDeleteItem(itemId);
    setShowDetailView(false);
    setSelectedItem(null);
  };

  const handleToggleFavoriteFromDetail = async (itemId: string) => {
    // Optimistic for detail view animation/color
    if (selectedItem && selectedItem.id === itemId) {
      setSelectedItem({ ...selectedItem, isFavorite: !selectedItem.isFavorite });
    }

    await toggleFavorite(itemId);

    // Sync with store after response
    if (selectedItem && selectedItem.id === itemId) {
      const updatedItem = items.find(i => i.id === itemId);
      if (updatedItem) {
        setSelectedItem(updatedItem);
      }
    }
  };

  const handleOpenMoveModal = (item: any) => {
    setItemToMove(item);
    setShowMoveModal(true);
    setOpenMenuId(null);
  };

  const handleMoveItem = async (categoryId: string | null) => {
    if (!itemToMove) return;
    try {
      const response = await api.patch(`/items/${itemToMove.id}/move`, { categoryId });
      dispatch(updateItem(response.data));
      setShowMoveModal(false);
      setItemToMove(null);
    } catch (error) {
      console.error('Failed to move item:', error);
      alert(t('library.moveFailed'));
    }
  };

  // Extract unique locations and tags from items
  const uniqueLocations: string[] = Array.from(new Set(items.map(item => item.location).filter(Boolean).filter((loc): loc is string => typeof loc === 'string')));
  const uniqueTags = Array.from(new Set(items.flatMap((item: any) => item.tags || [])));
  
  // Calculate max price from items for filter range
  const maxPrice = Math.max(10000, Math.ceil((Math.max(...items.map(i => i.purchasePrice || 0), 0) / 100)) * 100);

  const filteredItems = items.filter((item) => {
    const s = searchTerm.toLowerCase();
    const matchesSearch = item.name.toLowerCase().includes(s) ||
                         (item.description?.toLowerCase().includes(s)) ||
                         (item.location?.toLowerCase().includes(s)) ||
                         (item.category?.name?.toLowerCase().includes(s)) ||
                         ((item as any).tags || []).some((tag: string) => tag.toLowerCase().includes(s));
    const matchesCategory = !selectedCategory || item.categoryId === selectedCategory;
    
    // Advanced filters
    const matchesCondition = activeFilters.condition.length === 0 || 
                            activeFilters.condition.some(c => c.toUpperCase() === (item.condition || '').toUpperCase());
    
    const matchesValue = (activeFilters.valueRange[0] === 0 && activeFilters.valueRange[1] >= 10000) 
                        ? true 
                        : (item.purchasePrice != null && 
                           item.purchasePrice >= activeFilters.valueRange[0] && 
                           item.purchasePrice <= activeFilters.valueRange[1]);
    
    const matchesLocation = activeFilters.locations.length === 0 || 
                           activeFilters.locations.includes(item.location || '');
    
    const matchesTags = activeFilters.tags.length === 0 || 
                       activeFilters.tags.some(tag => (item as any).tags?.includes(tag));
    
    const matchesAIValue = !activeFilters.hasAIValue || !!(item as any).predictedValue;
    
    const matchesFavorites = !activeFilters.favoritesOnly || item.isFavorite;
    
    // Unit type filter
    const matchesUnitType = !activeFilters.unitTypes || activeFilters.unitTypes.length === 0 ||
                           activeFilters.unitTypes.includes((item as any).unitType || '');
    
    // Purchase date filters
    let matchesDateRange = true;
    if (activeFilters.purchaseDateFrom && (item as any).purchaseDate) {
      matchesDateRange = matchesDateRange && 
        new Date((item as any).purchaseDate) >= new Date(activeFilters.purchaseDateFrom);
    }
    if (activeFilters.purchaseDateTo && (item as any).purchaseDate) {
      matchesDateRange = matchesDateRange && 
        new Date((item as any).purchaseDate) <= new Date(activeFilters.purchaseDateTo);
    }
    
    return matchesSearch && matchesCategory && matchesCondition && matchesValue && 
           matchesLocation && matchesTags && matchesAIValue && matchesFavorites && matchesUnitType && matchesDateRange;
  });

  // Sort items - use the dropdown sortBy as the single source of truth
  const currentSortBy = sortBy;
  const sortedItems = [...filteredItems].sort((a, b) => {
    switch (currentSortBy) {
      case 'newest':
        return new Date((b as any).createdAt || 0).getTime() - new Date((a as any).createdAt || 0).getTime();
      case 'oldest':
        return new Date((a as any).createdAt || 0).getTime() - new Date((b as any).createdAt || 0).getTime();
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'value-high':
        return (b.purchasePrice || 0) - (a.purchasePrice || 0);
      case 'value-low':
        return (a.purchasePrice || 0) - (b.purchasePrice || 0);
      case 'condition-best':
        const conditionOrder = { 'NEW': 5, 'EXCELLENT': 4, 'GOOD': 3, 'FAIR': 2, 'POOR': 1 };
        return (conditionOrder[b.condition as keyof typeof conditionOrder] || 0) - (conditionOrder[a.condition as keyof typeof conditionOrder] || 0);
      case 'condition-worst':
        const conditionOrder2 = { 'NEW': 5, 'EXCELLENT': 4, 'GOOD': 3, 'FAIR': 2, 'POOR': 1 };
        return (conditionOrder2[a.condition as keyof typeof conditionOrder2] || 0) - (conditionOrder2[b.condition as keyof typeof conditionOrder2] || 0);
      default:
        return 0;
    }
  });

  if (loading) {
    return <div className="library-loading">{t('library.loading')}</div>;
  }

  return (
    <div className={`library ${viewMode === 'list' ? 'library-list-mode' : ''}`}>
      {/* Header */}
      <div className="library-header">
        <div className="library-search">
          <input
            type="text"
            placeholder={t('library.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <button onClick={() => handleOpenModal()} className="btn-add-item">
          {t('library.addItem')}
        </button>
      </div>

      {/* Item Modal */}
      <ItemModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onSave={handleSaveItem}
        item={editingItem}
        categories={categories}
        currencies={currencies}
      />

      {/* Filters */}
      <div className="library-filters">
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="filter-select"
        >
          <option value="">{t('library.allCategories')}</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
        
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="filter-select"
        >
          <option value="newest">{t('library.newestFirst')}</option>
          <option value="oldest">{t('library.oldestFirst')}</option>
          <option value="name-asc">{t('library.nameAZ')}</option>
          <option value="name-desc">{t('library.nameZA')}</option>
          <option value="value-high">{t('library.highestValue')}</option>
          <option value="value-low">{t('library.lowestValue')}</option>
        </select>

        <button 
          className="btn-advanced-filters"
          onClick={() => setShowAdvancedFilters(true)}
        >
          <FontAwesomeIcon icon={faFilter} /> {t('library.advancedFilters')}
          {(activeFilters.condition.length > 0 || 
            activeFilters.locations.length > 0 || 
            activeFilters.tags.length > 0 || 
            activeFilters.hasAIValue || 
            activeFilters.favoritesOnly) && 
            <span className="filter-badge">
              {activeFilters.condition.length + 
               activeFilters.locations.length + 
               activeFilters.tags.length + 
               (activeFilters.hasAIValue ? 1 : 0) + 
               (activeFilters.favoritesOnly ? 1 : 0)}
            </span>
          }
        </button>

        <div className="view-toggle">
          <button 
            className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title={t('library.gridView')}
          >
            <FontAwesomeIcon icon={faGrip} />
          </button>
          <button 
            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title={t('library.listView')}
          >
            <FontAwesomeIcon icon={faList} />
          </button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      <AdvancedFilters
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        onApply={(filters) => { setActiveFilters(filters); if (filters.sortBy) setSortBy(filters.sortBy); }}
        locations={uniqueLocations}
        tags={uniqueTags}
        maxPrice={maxPrice}
      />

      {/* Items Grid/List */}
      <div className={`items-container ${viewMode === 'list' ? 'items-container-list-mode' : ''}`}>
        {sortedItems.length > 0 ? (
          viewMode === 'grid' ? (
          <div className="items-grid">
            {sortedItems.map((item) => (
              <div 
                key={item.id} 
                className="item-card"
                onClick={() => handleViewItem(item)}
              >
                {/* Lock Badge */}
                {item.isLocked && (
                  <div className="card-lock-badge" title={t('library.locked') || 'Locked'}>
                    <FontAwesomeIcon icon={faLock} />
                  </div>
                )}

                {/* Favorite Button */}
                <button
                  className={`card-favorite-btn ${item.isFavorite ? 'is-active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(item.id);
                  }}
                  title={item.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="heart-icon">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </button>

                {/* Three Dot Menu */}
                <div className="card-menu-container">
                  <button
                    className="card-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === item.id ? null : item.id);
                    }}
                  >
                    <FontAwesomeIcon icon={faEllipsisH} />
                  </button>
                  {openMenuId === item.id && (
                    <div className="card-menu-dropdown">
                      <button onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(item);
                        setOpenMenuId(null);
                      }}>
                        <FontAwesomeIcon icon={faEdit} /> {t('library.edit')}
                      </button>
                      <button onClick={(e) => {
                        e.stopPropagation();
                        handleOpenMoveModal(item);
                        setOpenMenuId(null);
                      }}>
                        <FontAwesomeIcon icon={faArrowRight} /> {t('library.move')}
                      </button>
                      <button className="danger" onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteItem(item.id);
                        setOpenMenuId(null);
                      }}>
                        <FontAwesomeIcon icon={faTrash} /> {t('library.delete')}
                      </button>
                    </div>
                  )}
                </div>

                {/* Image */}
                <div
                  className="item-image"
                >
                  {(item.imageUrl || (item as any).images?.[0]?.url) && !imageErrorMap[item.id] ? (
                    <img
                      src={resolveFileUrl(item.imageUrl || (item as any).images?.[0]?.url) || undefined}
                      alt={item.name}
                      onError={() => setImageErrorMap(prev => ({ ...prev, [item.id]: true }))}
                    />
                  ) : (
                    <div className="no-image">
                      <FontAwesomeIcon icon={faBox} />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="item-info">
                  <div className="item-title-row">
                    <h4>{item.name}</h4>
                  </div>
                  {item.location && (
                    <p className="item-location">{item.location}</p>
                  )}
                  
                  {/* Badges */}
                  <div className="item-badges">
                    {item.category && (
                      <span 
                        className="badge category"
                        style={{ 
                          backgroundColor: `${item.category.color || '#667eea'}20`,
                          color: item.category.color || '#667eea',
                          borderColor: `${item.category.color || '#667eea'}40`
                        }}
                      >
                        {tc(item.category.name).toLowerCase()}
                      </span>
                    )}
                    {item.condition && item.condition !== 'N/A' && (
                      <span className={`badge condition ${item.condition?.toLowerCase()}`}>
                        {conditionLabel(item.condition)}
                      </span>
                    )}
                    {(item as any).unitType && (item as any).unitQuantity && (
                      <span className="badge unit-badge">
                        <FontAwesomeIcon icon={faRulerCombined} /> {formatQuantity((item as any).unitQuantity, (item as any).unitSubtype)}
                      </span>
                    )}
                  </div>

                  {/* Value */}
                  <div className="item-value-section">
                    <div className="value-row">
                      <span className="value-label">{t('library.value')}</span>
                      <span className="value-amount">
                        {item.purchasePrice 
                          ? formatPrice(
                              item.purchasePrice,
                              item.purchaseCurrency || 'USD',
                              userDefaultCurrency
                            )
                          : '-'
                        }
                      </span>
                    </div>
                    {item.purchaseCurrency && item.purchaseCurrency !== userDefaultCurrency && (
                      <div className="value-row original-currency">
                        <span className="value-label">{t('library.original')} ({item.purchaseCurrency})</span>
                        <span className="value-amount">
                          {item.purchasePrice?.toLocaleString()} {item.purchaseCurrency}
                        </span>
                      </div>
                    )}
                    {(item as any).predictedValue && (
                      <div className="value-row ai">
                        <span className="value-label">
                          {t('library.aiValue')} <FontAwesomeIcon icon={faWandMagicSparkles} style={{ fontSize: '0.9em' }} />
                        </span>
                        <span className="value-amount ai">
                          ${(item as any).predictedValue.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          ) : (
          /* List View */
          <div className="items-list full-page-list">
            {sortedItems.map((item) => (
              <div 
                key={item.id} 
                className="item-list-row"
                onClick={() => handleViewItem(item)}
              >
                {/* Thumbnail */}
                <div className="list-thumbnail">
                  {(item.imageUrl || (item as any).images?.[0]?.url) && !imageErrorMap[item.id] ? (
                    <img
                      src={resolveFileUrl(item.imageUrl || (item as any).images?.[0]?.url) || undefined}
                      alt={item.name}
                      onError={() => setImageErrorMap(prev => ({ ...prev, [item.id]: true }))}
                    />
                  ) : (
                    <div className="list-no-image">
                      <FontAwesomeIcon icon={faBox} />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="list-info">
                  <h4>
                    {item.isLocked && <FontAwesomeIcon icon={faLock} className="list-lock-icon" />}
                    {item.name}
                  </h4>
                  <div className="list-meta">
                    {item.category && (
                      <span 
                        className="badge category"
                        style={{ 
                          backgroundColor: `${item.category.color || '#667eea'}20`,
                          color: item.category.color || '#667eea',
                          borderColor: `${item.category.color || '#667eea'}40`
                        }}
                      >
                        {tc(item.category.name).toLowerCase()}
                      </span>
                    )}
                    {item.condition && item.condition !== 'N/A' && (
                      <span className={`badge condition ${item.condition?.toLowerCase()}`}>
                        {conditionLabel(item.condition)}
                      </span>
                    )}
                    {(item as any).unitType && (item as any).unitQuantity && (
                      <span className="badge unit-badge">
                        <FontAwesomeIcon icon={faRulerCombined} /> {formatQuantity((item as any).unitQuantity, (item as any).unitSubtype)}
                      </span>
                    )}
                    {item.location && (
                      <span className="list-location">
                        <FontAwesomeIcon icon={faMapMarkerAlt} /> {item.location}
                      </span>
                    )}
                  </div>
                </div>

                {/* Value */}
                <div className="list-value">
                  <span className="list-price">
                    {item.purchasePrice 
                      ? formatPrice(item.purchasePrice, item.purchaseCurrency || 'USD', userDefaultCurrency)
                      : '-'
                    }
                  </span>
                  {(item as any).predictedValue && (
                    <span className="list-ai-value">
                      <FontAwesomeIcon icon={faWandMagicSparkles} /> ${(item as any).predictedValue.toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="list-actions">
                  <button
                    className={`list-fav-btn ${item.isFavorite ? 'is-active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id); }}
                    title={item.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="heart-icon">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                  </button>
                  <button
                    className="list-edit-btn"
                    onClick={(e) => { e.stopPropagation(); handleOpenModal(item); }}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    className="list-delete-btn"
                    onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          )
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <FontAwesomeIcon icon={faBox} />
            </div>
            <h3>{t('library.noItems')}</h3>
            <p>{searchTerm ? t('library.noItemsSearch') : t('library.noItemsStart')}</p>
            <button onClick={() => handleOpenModal()} className="btn-primary">
              {t('library.addFirstItem')}
            </button>
          </div>
        )}

        {/* Item Detail View Modal */}
        {selectedItem && (
          <ItemDetailView
            item={selectedItem}
            isOpen={showDetailView}
            onClose={handleCloseDetailView}
            onEdit={handleEditFromDetail}
            onDelete={handleDeleteFromDetail}
            onToggleFavorite={handleToggleFavoriteFromDetail}
          />
        )}

        {/* Password Lock Modal */}
        {showLockModal && lockTargetItem && (
          <PasswordLockModal
            itemId={lockTargetItem.id}
            itemName={lockTargetItem.name}
            isLocked={true}
            mode="verify"
            onClose={() => { setShowLockModal(false); setLockTargetItem(null); }}
            onSuccess={handleLockSuccess}
          />
        )}
      </div>

      {/* Move Item Modal */}
      {showMoveModal && itemToMove && (
        <div className="modal-overlay" onClick={() => setShowMoveModal(false)}>
          <div className="move-modal" onClick={(e) => e.stopPropagation()}>
            <div className="move-modal-header">
              <h3>{t('library.moveItem')}</h3>
              <button className="close-btn" onClick={() => setShowMoveModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="move-modal-body">
              <p className="move-item-name">{t('library.moving')} <strong>{itemToMove.name}</strong></p>
              <p className="current-category">
                {t('library.currentCategory')} <span>{categories.find(c => c.id === itemToMove.categoryId)?.name || t('library.uncategorized')}</span>
              </p>
              <div className="category-select-list">
                <label>{t('library.selectCategory')}</label>
                {categories.filter(c => c.id !== itemToMove.categoryId).map(category => (
                  <button
                    key={category.id}
                    className="category-option"
                    onClick={() => handleMoveItem(category.id)}
                    style={{ borderLeftColor: category.color }}
                  >
                    <span className="category-color" style={{ backgroundColor: category.color }}></span>
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close menu */}
      {openMenuId && (
        <div 
          className="menu-overlay" 
          onClick={() => setOpenMenuId(null)}
        />
      )}

      {/* Plan Limit Modal */}
      <LimitReachedModal
        isOpen={showLimitModal}
        onClose={closeLimitModal}
        limitType={limitModalType}
        currentUsage={limitCurrent}
        maxAllowed={limitMax}
        planName={currentPlan}
      />
    </div>
  );
};

export default Library;
